import React from 'react';
import { Sparkles, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { ChatMessage as ChatMessageType } from './chat.ts';

interface ChatMessageProps {
  message: ChatMessageType;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Function to clean up markdown formatting by removing ** characters
  const cleanMarkdownText = (text: string): string => {
    // Remove ** (bold markdown) while preserving the text content
    return text.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*\*/g, '');
  };

  const rawMessageText = message.text || '';
  const messageText = cleanMarkdownText(rawMessageText);
  const isSystemMessage = messageText.includes('Welcome') || messageText.includes('System');
  const isErrorMessage = messageText.includes('error') || messageText.includes('Error') || messageText.includes('failed');
  const hasActions = messageText.includes('calendar') || messageText.includes('task') || messageText.includes('budget');

  if (isSystemMessage) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg px-4 py-2 max-w-md text-center">
          <div className="flex items-center justify-center space-x-2 text-blue-600">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Wedding Assistant</span>
          </div>
          <p className="text-sm text-blue-700 mt-1">{messageText}</p>
          <div className="text-xs text-blue-500 mt-1">
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start space-x-3 ${message.isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
      {/* Avatar */}
      {!message.isUser && (
        <div className="bg-gradient-to-br from-pink-100 to-pink-200 p-2 rounded-full flex-shrink-0 shadow-sm">
          <Sparkles className="w-4 h-4 text-pink-600" />
        </div>
      )}
      
      {message.isUser && (
        <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-2 rounded-full flex-shrink-0 shadow-sm">
          <div className="w-4 h-4 text-blue-600 font-bold text-xs flex items-center justify-center">
            U
          </div>
        </div>
      )}

      {/* Message Content */}
      <div className={`max-w-[75%] ${message.isUser ? 'items-end' : 'items-start'} flex flex-col space-y-1`}>
        {/* Message Bubble */}
        <div
          className={`rounded-2xl px-4 py-2 shadow-sm ${
            message.isUser
              ? 'bg-gradient-to-br from-pink-600 to-pink-700 text-white rounded-br-md'
              : isErrorMessage
              ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
              : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
          }`}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{messageText}</p>
          
          {/* Action Indicators for Agent Messages */}
          {!message.isUser && hasActions && (
            <div className="mt-2 flex items-center space-x-2 text-xs">
              <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full">
                <CheckCircle className="w-3 h-3" />
                <span>Action completed</span>
              </div>
            </div>
          )}
          
          {/* Error Indicator */}
          {!message.isUser && isErrorMessage && (
            <div className="mt-2 flex items-center space-x-1 text-xs text-red-600">
              <AlertCircle className="w-3 h-3" />
              <span>Please try again</span>
            </div>
          )}
        </div>

        {/* Message Meta Information */}
        <div className={`flex items-center space-x-2 text-xs text-gray-500 px-1 ${
          message.isUser ? 'flex-row-reverse space-x-reverse' : ''
        }`}>
          <span className="font-medium">
            {message.isUser ? 'You' : 'Wedding Agent'}
          </span>
          <span>•</span>
          <div className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{formatTimestamp(message.timestamp)}</span>
          </div>
          
          {/* Status Indicator */}
          {!message.isUser && (
            <>
              <span>•</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Delivered</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};